import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import {HTTP_INTERCEPTORS, HttpClientModule} from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule, Routes } from '@angular/router';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import 'hammerjs';

import { FuseModule } from '@fuse/fuse.module';
import { FuseSharedModule } from '@fuse/shared.module';
import { FuseProgressBarModule, FuseSidebarModule, FuseThemeOptionsModule } from '@fuse/components';

import { fuseConfig } from 'app/fuse-config';

import { AppComponent } from 'app/app.component';
import { LayoutModule } from 'app/layout/layout.module';
import {AuthGuard} from "./main/helpers/auth.guard";
import {UserComponent} from "./main/user/user.component";
import {NewUserComponent} from "./main/user/newUser.component";
import {GroupComponent} from "./main/group/group.component";
import {OperatorComponent} from "./main/operator/operator.component";
import {AddEditOperatorComponent} from "./main/operator/addEditOperator.component";
import {ProfileComponent} from "./main/profile/profile.component";
import {UserModule} from "./main/user/user.module";
import {GroupModule} from "./main/group/group.module";
import {OperatorModule} from "./main/operator/operator.module";
import {ProfileModule} from "./main/profile/profile.module";
import {LoginModule} from "./main/login/login.module";
import {LoginComponent} from "./main/login/login.component";
import {DashboardModule} from "./main/dashboard/dashboard.module";
import {ErrorInterceptor} from "./main/helpers/error.interceptor";
import {CustomerModule} from "./main/customer/customer.module";
import {CustomerComponent} from "./main/customer/customer.component";
import {CustomerFormComponent} from "./main/customer/customerForm.component";
import {SettingsComponent} from "./main/settings/settings.component";
import {SettingsModule} from "./main/settings/settings.module";
import {DeviceModule} from "./main/device/device.module";
import {DeviceComponent} from "./main/device/device.component";
import {DeviceFormComponent} from "./main/device/deviceForm.component";
import {DeviceGroupModule} from "./main/deviceGroup/deviceGroup.module";
import {DeviceGroupComponent} from "./main/deviceGroup/deviceGroup.component";
import {DeviceGroupFormComponent} from "./main/deviceGroup/deviceGroupForm.component";
import {CustomerSourceComponent} from "./main/customerSource/customerSource.component";
import {CustomerSourceFormComponent} from "./main/customerSource/customerSourceForm.component";
import {CustomerSourceModule} from "./main/customerSource/customerSource.module";
import {ProductModule} from "./main/product/product.module";
import {ProductComponent} from "./main/product/product.component";
import {ProductFormComponent} from "./main/product/productForm.component";
import {MatDialogModule} from "@angular/material/dialog";
import {CategoryModule} from "./main/category/category.module";
import {VatRateModule} from "./main/vatRate/vatRate.module";
import {VatRateComponent} from "./main/vatRate/vatRate.component";
import {VatRateFormComponent} from "./main/vatRate/vatRateForm.component";
import {ManufacturerModule} from "./main/manufacturer/manufacturer.module";
import {ServiceModule} from "./main/service/service.module";
import {ServiceComponent} from "./main/service/service.component";
import {ResourceModule} from "./main/resource/resource.module";
import {ResourceComponent} from "./main/resource/resource.component";
import {ResourceFormComponent} from "./main/resource/resourceForm.component";
import {ResourceDetailsComponent} from "./main/resource/resourceDetails.component";
import {ServiceDetailsComponent} from "./main/service/serviceDetails.component";
import {CustomerDetailsComponent} from "./main/customer/customerDetails.component";
import {CustomerPersonalDataComponent} from "./main/customer/customerPersonalData.component";
import {ServiceFormComponent} from "./main/service/serviceForm.component";
import {CalendarComponent} from "./main/calendar/calendar.component";
import {CalendarModule} from "./main/calendar/calendar.module";
import {OrderModule} from "./main/order/order.module";
import {OrderComponent} from "./main/order/order.component";
import {SupplierComponent} from "./main/supplier/supplier.component";
import {SupplierModule} from "./main/supplier/supplier.module";
import {SupplierOrderModule} from "./main/supplierOrder/supplierOrder.module";
import {SupplierOrderComponent} from "./main/supplierOrder/supplierOrder.component";
import {MatTabsModule} from "@angular/material/tabs";
import {SupplierOrderFormComponent} from "./main/supplierOrder/supplierOrderForm.component";
import {MatSnackBarModule} from "@angular/material/snack-bar";
import {AlertComponent} from "./main/alert/alert.component";
import {VoucherTemplateModule} from "./main/voucherTemplate/voucherTemplate.module";
import {VoucherTemplateComponent} from "./main/voucherTemplate/voucherTemplate.component";
import {VoucherTemplateFormComponent} from "./main/voucherTemplate/voucherTemplateForm.component";
import {NewOrderComponent} from "./main/order/newOrder.component";
import {ManufacturerComponent} from "./main/manufacturer/manufacturer.component";
import {ManufacturerFormComponent} from "./main/manufacturer/manufacturerForm.component";
import {SupplierFormComponent} from "./main/supplier/supplierForm.component";

const accountModule = () => import('./main/account/account.module').then(x => x.AccountModule);
const securityModule = () => import('./main/security/security.module').then(x => x.SecurityModule);

const appRoutes: Routes = [
    // {
    //     path: '',
    //     component: DashboardModule,
    //     canActivate: [AuthGuard]
    // },
    // {
    //     path: '',
    //     component: DashboardComponent,
    //     canActivate: [AuthGuard]
    // },
    {
        path: '',
        component: CustomerComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST'],
            module: 'Customers'
        }
    },
    {
        path: 'login',
        component: LoginComponent
    },
    {
        path: 'users',
        component: UserComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        }
    },
    {
        path: 'trainer-dashboard',
        component: TrainerDashboardComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_TRAINER']
        }
    },
    {
        path: 'training',
        component: TrainingComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_TRAINER']
        }
    },
    {
        path: 'training/:id',
        component: TrainingComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_TRAINER'],
        },
        resolve: {
            reservation: ReservationDataResolve,
        }
    },
    {
        path: 'training-details/:id',
        component: TrainingDetailsComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        }
    },
    {
        path: 'dashboard',
        component: DashboardComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        }
    },
    {
        path: 'trainers',
        component: TrainerComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        }
    },
    {
        path: 'users/new',
        component: NewUserComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        },
        resolve: {
            countries: CountriesResolve,
            services: ServicesResolve,
        }
    },
    {
        path: 'users/:cardNumber/new',
        component: NewUserComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        },
        resolve: {
            countries: CountriesResolve,
            services: ServicesResolve,
        }
    },
    {
        path: 'users/edit/:id',
        component: NewUserComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        },
        resolve: {
            userData: UserDataResolve,
            countries: CountriesResolve,
            services: ServicesResolve,
        }
    },
    {
        path: 'groups',
        component: GroupComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN']
        }
    },
    {
        path: 'account',
        loadChildren: accountModule
    },
    {
        path: 'security-settings',
        loadChildren: securityModule
    },
    {
        path: 'operators',
        component: OperatorComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN']
        }
    },
    {
        path: 'operators/new',
        component: AddEditOperatorComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN']
        }
    },
    {
        path: 'operators/edit/:id',
        component: AddEditOperatorComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        }
    },
    {
        path: 'profile',
        component: ProfileComponent,
        canActivate: [AuthGuard]
    },
    {
        path: 'customers',
        component: CustomerComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST'],
            module: 'Customers'
        }
    },
    {
        path: 'customers/registered/from/:from/to/:to',
        component: CustomerComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST'],
            module: 'Customers'
        }
    },
    {
        path: 'customers/first-sale/from/:firstSaleFrom/to/:firstSaleTo',
        component: CustomerComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST'],
            module: 'Customers'
        }
    },
    {
        path: 'customers/first-online-sale/from/:firstOnlineSaleFrom/to/:firstOnlineSaleTo',
        component: CustomerComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST'],
            module: 'Customers'
        }
    },
    {
        path: 'customers/first-traditional-sale/from/:firstTraditionalSaleFrom/to/:firstTraditionalSaleTo',
        component: CustomerComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST'],
            module: 'Customers'
        }
    },
    {
        path: 'customers/new',
        component: CustomerFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST'],
            module: 'Customers'
        }
    },
    {
        path: 'customers/edit/:id',
        component: CustomerFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST'],
            module: 'Customers'
        },
        resolve: {
            userData: UserDataResolve,
        }
    },
    {
        path: 'customers/details/:id',
        component: CustomerDetailsComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST'],
            module: 'Customers'
        },
        resolve: {
            userData: UserDataResolve,
        }
    },
    {
        path: 'customers/personal-data/:id',
        component: CustomerPersonalDataComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST'],
            module: 'Customers'
        },
        resolve: {
            userData: UserDataResolve,
        }
    },
    {
        path: 'customers/cards/:id',
        component: CustomerCardsComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST'],
            module: 'Customers'
        },
        resolve: {
            userData: UserDataResolve,
        }
    },
    {
        path: 'customers/communication/:id',
        component: CustomerCommunicationComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST'],
            module: 'Customers'
        },
        resolve: {
            userData: UserDataResolve,
        }
    },
    {
        path: 'cards',
        component: CardComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST'],
            module: 'Customers'
        },
        resolve: {

        }
    },
    {
        path: 'customers/cards/:id/add',
        component: AddCardComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST'],
            module: 'Customers'
        },
        resolve: {
            userData: UserDataResolve,
        }
    },
    {
        path: 'customers/cards/:cardId/add-item/:id',
        component: AddCardItemComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST'],
            module: 'Customers'
        },
        resolve: {
            userData: UserDataResolve,
            cardData: CardResolve,
        }
    },
    {
        path: 'customers/cards/:cardUuid/history/:id',
        component: CardHistoryComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST'],
            module: 'Customers'
        },
        resolve: {
            userData: UserDataResolve,
            cardData: CardByUuidResolve,
        }
    },
    {
        path: 'customers/cards/:cardUuid/error-logs/:id',
        component: CardErrorLogsComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST'],
            module: 'Customers'
        },
        resolve: {
            userData: UserDataResolve,
            cardData: CardByUuidResolve,
        }
    },
    {
        path: 'customers/resources-in-use',
        component: ResourcesInUseComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST'],
            module: 'Customers'
        },
        resolve: {

        }
    },
    {
        path: 'customers/reservations/:id',
        component: ReservationComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST'],
            module: 'Customers'
        },
        resolve: {
            userData: UserDataResolve,
        }
    },
    {
        path: 'settings',
        component: SettingsComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN']
        }
    },
    {
        path: 'maximal-quantity-of-opened-tabs',
        component: TabsComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_USER']
        }
    },
    {
        path: 'current-version',
        component: CurrentVersionComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST', 'ROLE_WORKEER']
        }
    },
    {
        path: 'devices',
        component: DeviceComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN'],
            module: 'Resources'
        }
    },
    {
        path: 'devices/new',
        component: DeviceFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN'],
            module: 'Resources'
        }
    },
    {
        path: 'devices/edit/:id',
        component: DeviceFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN'],
            module: 'Resources'
        }
    },
    {
        path: 'device-groups',
        component: DeviceGroupComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN'],
            module: 'Resources'
        }
    },
    {
        path: 'device-groups/new',
        component: DeviceGroupFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN'],
            module: 'Resources'
        }
    },
    {
        path: 'device-groups/edit/:id',
        component: DeviceGroupFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN'],
            module: 'Resources'
        }
    },
    {
        path: 'customer-sources',
        component: CustomerSourceComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN']
        }
    },
    {
        path: 'customer-sources/new',
        component: CustomerSourceFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN']
        }
    },
    {
        path: 'customer-sources/edit/:id',
        component: CustomerSourceFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN']
        }
    },
    {
        path: 'products',
        component: ProductComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST'],
            module: 'Products'
        }
    },
    {
        path: 'products/new',
        component: ProductFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST'],
            module: 'Products'
        }
    },
    {
        path: 'products/edit/:id',
        component: ProductFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST'],
            module: 'Products'
        }
    },
    {
        path: 'products/stocks/:id',
        component: StockComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST'],
            module: 'Products'
        }
    },
    {
        path: 'products/sale-report/:id',
        component: ProductSaleReportComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_ADMIN', 'ROLE_MANAGER'],
            module: 'Products'
        },
        resolve: {
            product: ProductResolve,
            totalSale: ProductTotalSummaryResolve,
            totalSaleByYear: ProductTotalSummaryByYearResolve,
        }
    },
    {
        path: 'settings/vat-rates',
        component: VatRateComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN']
        }
    },
    {
        path: 'settings/vat-rates/new',
        component: VatRateFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_ADMIN', 'ROLE_MANAGER']
        }
    },
    {
        path: 'settings/vat-rates/edit/:id',
        component: VatRateFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_ADMIN', 'ROLE_MANAGER']
        }
    },
    {
        path: 'services',
        component: ServiceComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        }
    },
    {
        path: 'services/new',
        component: ServiceFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        },
        resolve:{
            resources: AllResourcesResolve,
            vatRates: VatRatesResolve,
            categories: ServiceCategoriesResolve,
        }
    },
    {
        path: 'services/edit/:id',
        component: ServiceFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        },
        resolve:{
            service: ServiceResolve,
            resources: AllResourcesResolve,
            vatRates: VatRatesResolve,
            categories: ServiceCategoriesResolve,
        }
    },
    {
        path: 'settings/resources',
        component: ResourceComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST'],
        }
    },
    {
        path: 'settings/resources/new',
        component: ResourceFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_ADMIN', 'ROLE_MANAGER']
        },
        resolve: {
            resourceTypes: AllResourceTypesResolve,
        }
    },
    {
        path: 'settings/resources/details/:id',
        component: ResourceDetailsComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        },
        resolve: {
            resource: ResourceResolve,
        }
    },
    {
        path: 'settings/resources/edit/:id',
        component: ResourceFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_ADMIN', 'ROLE_MANAGER']
        },
        resolve: {
            resourceTypes: AllResourceTypesResolve,
            resource: ResourceResolve,
        }
    },
    {
        path: 'services/details/:id',
        component: ServiceDetailsComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        }
    },
    // {
    //     path: 'calendar',
    //     component: CalendarComponent,
    //     canActivate: [AuthGuard],
    //     data: {
    //         roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
    //     }
    // },
    {
        path: 'calendar',
        component: CalendarComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        },
        resolve:{
            workers: CalendarWorkersResolve,
            resources: CalendarResourceUsersResolve,
        }
    },
    {
        path: 'calendar/group-activity/:id',
        component: GroupActivityComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST', 'ROLE_TRAINER']
        },
        resolve: {
            reservation: ReservationDataResolve,
        }
    },
    {
        path: 'employee-schedule',
        component: EmployeeScheduleComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        },
        resolve:{
            workers: WorkersResolve,
        }
    },
    {
        path: 'employee-schedule/:id',
        component: SelectedEmpoyeeScheduleComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        },
        resolve:{
            worker: UserDataResolve,
        }
    },
    {
        path: 'calendar/:timePeriod',
        component: CalendarComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        }
    },
    {
        path: 'orders',
        component: OrderComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        }
    },
    {
        path: 'customers/orders/:userId',
        component: OrderComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        }
    },
    {
        path: 'orders/details/:id',
        component: OrderDetailsComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        },
        resolve:{
            order: OrderResolve,
        }
    },
    {
        path: 'orders/new',
        component: NewOrderComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        },
        resolve: {
            activeCashReport: ActiveCashReportResolve,
        }
    },
    {
        path: 'orders/new/:type/:productId',
        component: NewOrderComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        },
        resolve: {
            activeCashReport: ActiveCashReportResolve,
        }
    },
    {
        path: 'pos',
        component: PosComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        },
        resolve: {
            activeCashReport: ActiveCashReportResolve,
        }
    },
    {
        path: 'pos/:customerId',
        component: PosComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        },
        resolve: {
            activeCashReport: ActiveCashReportResolve,
            customer: CustomerResolve,
        }
    },
    {
        path: 'open-bills',
        component: VirtualReceiptComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        },
    },
    {
        path: 'suppliers',
        component: SupplierComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER'],
            module: 'ASN',
            permission: 'READ'
        }
    },
    {
        path: 'suppliers/new',
        component: SupplierFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER'],
            module: 'ASN',
            permission: 'CREATE'
        }
    },
    {
        path: 'suppliers/edit/:id',
        component: SupplierFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER'],
            module: 'ASN',
            permission: 'UPDATE'
        }
    },
    {
        path: 'supplier-orders',
        component: SupplierOrderComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER'],
            module: 'ASN',
            permission: 'READ'
        }
    },
    {
        path: 'supplier-orders/new',
        component: SupplierOrderFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER'],
            module: 'ASN',
            permission: 'CREATE'
        }
    },
    {
        path: 'supplier-orders/:id',
        component: SupplierOrderDetailsComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER'],
            module: 'ASN',
            permission: 'READ'
        }
    },
    {
        path: 'settings/voucher-templates',
        component: VoucherTemplateComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        }
    },
    {
        path: 'settings/voucher-templates/sorting',
        component: VoucherTemplateSortingComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        }
    },
    {
        path: 'settings/voucher-templates/new',
        component: VoucherTemplateFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        }
    },
    {
        path: 'settings/voucher-templates/edit/:id',
        component: VoucherTemplateFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        }
    },
    {
        path: 'settings/discounts',
        component: DiscountComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        }
    },
    {
        path: 'settings/discounts/new',
        component: DiscountFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        },
        resolve: {
            voucherGroups: VoucherGroupsResolve,
        }
    },
    {
        path: 'settings/discounts/edit/:id',
        component: DiscountFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        },
        resolve: {
            discount: DiscountResolve,
            voucherGroups: VoucherGroupsResolve,
        }
    },
    {
        path: 'manufacturers',
        component: ManufacturerComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        }
    },
    {
        path: 'manufacturers/new',
        component: ManufacturerFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        }
    },
    {
        path: 'manufacturers/edit/:id',
        component: ManufacturerFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        }
    },
    {
        path: 'reports',
        component: ReportComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']
        }
    },
    {
        path: 'reports/services-use',
        component: ServicesUseComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']
        }
    },
    {
        path: 'reports/vouchers-report',
        component: VouchersReportComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']
        }
    },
    {
        path: 'reports-list',
        component: ReportListComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']
        }
    },
    {
        path: 'reports/new-customers',
        component: NewCustomersComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']
        }
    },
    {
        path: 'reports/first-sale',
        component: FirstSaleComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']
        }
    },
    {
        path: 'reports/receipt-errors',
        component: ErrorReceiptsComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']
        }
    },
    {
        path: 'reports/work-time',
        component: WorkTimeReportComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']
        }
    },
    {
        path: 'uses',
        component: StorehouseUseComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        }
    },
    {
        path: 'uses/details/:id',
        component: StorehouseUseDetailsComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        }
    },
    {
        path: 'uses/new',
        component: StorehouseUseFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        }
    },
    {
        path: 'users-online',
        component: CardOnlineComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        }
    },
    {
        path: 'actions',
        component: SignUpActionComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        }
    },
    {
        path: 'actions/new',
        component: SignUpActionFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        }
    },
    {
        path: 'actions/edit/:id',
        component: SignUpActionFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        }
    },
    {
        path: 'settings/public-api-accesses',
        component: PublicApiAccessComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        }
    },
    {
        path: 'settings/public-api-accesses/new',
        component: PublicApiAccessFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        }
    },
    {
        path: 'settings/public-api-accesses/edit/:id',
        component: PublicApiAccessFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        }
    },
    {
        path: 'settings/message-templates',
        component: MessageTemplateComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        }
    },
    {
        path: 'settings/message-templates/new',
        component: MessageTemplateFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        },
        resolve: {
            notificationAccounts: NotificationAccountListResolve,
        }
    },
    {
        path: 'settings/message-templates/edit/:id',
        component: MessageTemplateFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        },
        resolve: {
            notificationAccounts: NotificationAccountListResolve,
        }
    },
    {
        path: 'settings/notification-accounts',
        component: NotificationAccountComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        }
    },
    {
        path: 'settings/notification-accounts/new',
        component: NotificationAccountFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        }
    },
    {
        path: 'settings/notification-accounts/edit/:id',
        component: NotificationAccountFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        }
    },
    {
        path: 'settings/notifications',
        component: NotificationComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        }
    },
    {
        path: 'settings/notifications/new',
        component: NotificationFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        },
        resolve: {
            messageTemplates: MessageTemplatesResolve,
        }
    },
    {
        path: 'settings/notifications/edit/:id',
        component: NotificationFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        },
        resolve: {
            messageTemplates: MessageTemplatesResolve,
            notification: NotificationResolve,
        }
    },
    {
        path: 'settings/marketing-actions',
        component: MarketingActionComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        }
    },
    {
        path: 'settings/marketing-actions/new',
        component: MarketingActionFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        }
    },
    {
        path: 'settings/marketing-actions/edit/:id',
        component: MarketingActionFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        }
    },
    {
        path: 'customers/resources-online',
        component: ResourceOnlineComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        }
    },
    {
        path: 'customers/card-stats/:uuid',
        component: CardStatsComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        }
    },
    {
        path: 'customers/active-vouchers',
        component: ActiveVouchersComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        }
    },
    {
        path: 'customers/card-stats/:cardUuid/details/:uuid',
        component: CardStatsDetailsComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        }
    },
    {
        path: 'customers/notification-history/:id',
        component: NotificationHistoryComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        }
    },
    {
        path: 'files',
        component: InternalFileComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']
        }
    },
    {
        path: 'files/new',
        component: InternalFileFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']
        }
    },
    // {
    //     path: 'files/details/:id',
    //     component: FileComponent,
    //     canActivate: [AuthGuard],
    //     data: {
    //         roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']
    //     },
    //     resolve: {
    //         file: FileResolve,
    //     }
    // },
    {
        path: 'cms',
        component: CmsComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']
        }
    },
    {
        path: 'cms/galleries',
        component: GalleryComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']
        }
    },
    {
        path: 'cms/galleries/new',
        component: GalleryFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']
        }
    },
    {
        path: 'cms/galleries/edit/:id',
        component: GalleryFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']
        },
        resolve: {
            languages: LanguagesResolve,
        }
    },
    {
        path: 'cms/galleries/details/:id',
        component: GalleryDetailsComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']
        },
        resolve: {
            gallery: GalleryResolve,
            languages: LanguagesResolve,
        }
    },
    {
        path: 'cms/languages',
        component: LanguageComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']
        }
    },
    {
        path: 'cms/languages/new',
        component: LanguageFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']
        }
    },
    {
        path: 'cms/languages/edit/:id',
        component: LanguageFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']
        },
        resolve: {
            language: LanguageResolve,
        }
    },
    {
        path: 'cms/menus',
        component: MenuComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']
        }
    },
    {
        path: 'cms/menus/new',
        component: MenuFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']
        },
        resolve: {
            languages: LanguagesResolve,
        }
    },
    {
        path: 'cms/menus/edit/:id',
        component: MenuFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']
        },
        resolve: {
            menu: MenuResolve,
            languages: LanguagesResolve,
        }
    },
    {
        path: 'logs/:referenceId/:objectReference',
        component: LogComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']
        },
        resolve: {
        }
    },
    {
        path: 'cms/pages',
        component: PageComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']
        }
    },
    {
        path: 'cms/pages/new',
        component: PageFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']
        },
        resolve: {
            languages: LanguagesResolve,
        }
    },
    {
        path: 'cms/pages/edit/:id',
        component: PageFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']
        },
        resolve: {
            page: PageResolve,
            languages: LanguagesResolve,
        }
    },
    {
        path: 'cash-reports',
        component: CashReportComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']
        },
        resolve: {
            activeCashReport: ActiveCashReportResolve,
            lastCashReport: LastCashReportResolve,
        }
    },
    {
        path: 'reports/multi-sport',
        component: MultiSportComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']
        }
    },
    {
        path: 'reports/medicover',
        component: MedicoverComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']
        }
    },
    {
        path: 'reports/pzu-sport',
        component: PzuSportComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']
        }
    },
    {
        path: 'reports/profit',
        component: ProfitComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']
        }
    },
    {
        path: 'reports/training-commission',
        component: TrainingCommissionComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']
        }
    },
    {
        path: 'cash-reports/show/:id',
        component: CashReportDetailsComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER']
        },
        resolve: {
            cashReport: CashReportResolve,
            activeCashReport: ActiveCashReportResolve,
        }
    },
    {
        path: 'settings/voucher-template-groups',
        component: VoucherTemplateGroupComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        }
    },
    {
        path: 'settings/voucher-template-groups/new',
        component: VoucherTemplateGroupFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        }
    },
    {
        path: 'settings/voucher-template-groups/edit/:id',
        component: VoucherTemplateGroupFormComponent,
        canActivate: [AuthGuard],
        data: {
            roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_RECEPTIONIST']
        }
    },
    {
        path: 'notes',
        component: NoteComponent,
        canActivate: [AuthGuard],
        data: {

        }
    },
    {
        path: 'carts',
        component: CartComponent,
        canActivate: [AuthGuard],
        data: {

        }
    },
];

import dayGridPlugin from '@fullcalendar/daygrid'; // a plugin
import interactionPlugin from '@fullcalendar/interaction';
import {SupplierOrderDetailsComponent} from "./main/supplierOrder/supplierOrderDetails.component";
import {ReportModule} from "./main/report/report.module";
import {ReportComponent} from "./main/report/report.component";
import {StorehouseUseModule} from "./main/storehouseUse/storehouseUse.module";
import {StorehouseUseComponent} from "./main/storehouseUse/storehouseUse.component";
import {StorehouseUseFormComponent} from "./main/storehouseUse/storehouseUseForm.component";
import {StorehouseUseDetailsComponent} from "./main/storehouseUse/storehouseUseDetails.component";
import {CalendarModule as AngularCalendarModule, DateAdapter} from 'angular-calendar'; // a plugin
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import {WorkersResolve} from "./main/user/workers.resolve";
import {ResourcesResolve} from "./main/resource/resources.resolve";
import {UserDataResolve} from "./main/user/userData.resolve";
import {CountriesResolve} from "./main/country/countries.resolve";
import {CountryModule} from "./main/country/country.module";
import {ServicesResolve} from "./main/service/services.resolve";
import {ResourceUsersResolve} from "./main/user/resourceUsersResolve.resolve";
import {EmployeeScheduleComponent} from "./main/employeeSchedule/employeeSchedule.component";
import {EmployeeScheduleModule} from "./main/employeeSchedule/employeeSchedule.module";
import {CustomerCardsComponent} from "./main/customer/customerCards.component";
import {AddCardComponent} from "./main/customer/addCard.component";
import {AddCardItemComponent} from "./main/customer/addCardItem.component";
import {CardResolve} from "./main/card/card.resolve";
import {CardModule} from "./main/card/card.module";
import {CardHistoryComponent} from "./main/card/cardHistory.component";
import {CardByUuidResolve} from "./main/card/cardByUuid.resolve";
import {ResourcesInUseComponent} from "./main/customer/resourcesInUse.component";
import {CardComponent} from "./main/card/card.component";
import {PosModule} from "./main/pos/pos.module";
import {PosComponent} from "./main/pos/pos.component";
import {CardOnlineModule} from "./main/cardOnline/cardOnline.module";
import {CardOnlineComponent} from "./main/cardOnline/cardOnline.component";
import {OrderDetailsComponent} from "./main/order/orderDetails.component";
import {OrderResolve} from "./main/order/order.resolve";
import {SignUpActionModule} from "./main/signUpAction/signUpAction.module";
import {SignUpActionComponent} from "./main/signUpAction/signUpAction.component";
import {SignUpActionFormComponent} from "./main/signUpAction/signUpActionForm.component";
import {PublicApiAccessModule} from "./main/publicApiAccess/publicApiAccess.module";
import {PublicApiAccessComponent} from "./main/publicApiAccess/publicApiAccess.component";
import {PublicApiAccessFormComponent} from "./main/publicApiAccess/publicApiAccessForm.component";
import {MessageTemplateModule} from "./main/messageTemplate/messageTemplate.module";
import {MessageTemplate} from "./main/models/messageTemplate";
import {MessageTemplateComponent} from "./main/messageTemplate/messageTemplate.component";
import {MessageTemplateFormComponent} from "./main/messageTemplate/messageTemplateForm.component";
import {NotificationAccountModule} from "./main/notificationAccount/notificationAccount.module";
import {NotificationAccountComponent} from "./main/notificationAccount/notificationAccount.component";
import {NotificationAccountFormComponent} from "./main/notificationAccount/notificationAccountForm.component";
import {MarketingActionModule} from "./main/marketingAction/marketingAction.module";
import {MarketingActionComponent} from "./main/marketingAction/marketingAction.component";
import {MarketingActionFormComponent} from "./main/marketingAction/marketingActionForm.component";
import {ResourceOnlineComponent} from "./main/customer/resourceOnline.component";
import {SecurityModule} from "./main/security/security.module";
import {CardStatsComponent} from "./main/cardStats/cardStats.component";
import {CardStatsModule} from "./main/cardStats/cardStats.module";
import {CardStatsDetailsComponent} from "./main/cardStats/cardStatsDetails.component";
import {CustomerCommunicationComponent} from "./main/customer/customerCommunication.component";
import {CmsModule} from "./main/cms/cms.module";
import {GalleryModule} from "./main/cms/gallery/gallery.module";
import {GalleryComponent} from "./main/cms/gallery/gallery.component";
import {GalleryFormComponent} from "./main/cms/gallery/galleryForm.component";
import {GalleryResolve} from "./main/cms/gallery/resolvers/gallery.resolve";
import {GalleryDetailsComponent} from "./main/cms/gallery/galleryDetails.component";
import {LanguageModule} from "./main/cms/language/language.module";
import {LanguageComponent} from "./main/cms/language/language.component";
import {MenuModule} from "./main/cms/menu/menu.module";
import {PageModule} from "./main/cms/page/page.module";
import {CmsComponent} from "./main/cms/cms.component";
import {LanguageFormComponent} from "./main/cms/language/languageForm.component";
import {LanguageResolve} from "./main/cms/language/resolvers/language.resolve";
import {MenuComponent} from "./main/cms/menu/menu.component";
import {MenuFormComponent} from "./main/cms/menu/menuForm.component";
import {MenuResolve} from "./main/cms/menu/resolvers/menu.resolve";
import {PageComponent} from "./main/cms/page/page.component";
import {PageFormComponent} from "./main/cms/page/pageForm.component";
import {PageResolve} from "./main/cms/page/resolvers/page.resolve";
import {LanguagesResolve} from "./main/cms/language/resolvers/languages.resolve";
import {CashReportModule} from "./main/cashReport/cashReport.module";
import {CashReportComponent} from "./main/cashReport/cashReport.component";
import {CashReportDetailsComponent} from "./main/cashReport/cashReportDetails.component";
import {CashReportResolve} from "./main/cashReport/resolvers/cashReport.resolve";
import {ActiveCashReportResolve} from "./main/cashReport/resolvers/activeCashReport.resolve";
import {LastCashReportResolve} from "./main/cashReport/resolvers/lastCashReport.resolve";
import {VoucherTemplateGroupModule} from "./main/voucherTemplateGroup/voucherTemplateGroup.module";
import {VoucherTemplateGroupComponent} from "./main/voucherTemplateGroup/voucherTemplateGroup.component";
import {VoucherTemplateGroupFormComponent} from "./main/voucherTemplateGroup/voucherTemplateGroupForm.component";
import {StockComponent} from "./main/product/stock.component";
import {NotificationAccountList} from "./main/models/notificationAccountList";
import {NotificationAccountListResolve} from "./main/notificationAccount/resolvers/notificationAccountList.resolve";
import {InternalFileModule} from "./main/internalFile/internalFile.module";
import {InternalFileComponent} from "./main/internalFile/internalFile.component";
import {InternalFileFormComponent} from "./main/internalFile/internalFileForm.component";
import {ReportListComponent} from "./main/report/reportList.component";
import {NewCustomersComponent} from "./main/report/newCustomers.component";
import {FirstSaleComponent} from "./main/report/firstSale.component";
import {ErrorReceiptsComponent} from "./main/report/errorReceipts.component";
import {ReservationComponent} from "./main/reservation/reservation.component";
import {ReservationModule} from "./main/reservation/reservation.module";
import {NotificationModule} from "./main/notification/notification.module";
import {NotificationComponent} from "./main/notification/notification.component";
import {MessageTemplatesResolve} from "./main/messageTemplate/resolvers/messageTemplates.resolver";
import {NotificationResolve} from "./main/notification/resolvers/notification.resolve";
import {NotificationFormComponent} from "./main/notification/notificationForm.component";
import {NotificationHistoryComponent} from "./main/notificationHistory/notificationHistory.component";
import {NotificationHistoryModule} from "./main/notificationHistory/notificationHistory.module";
import {NoteModule} from "./main/note/note.module";
import {NoteComponent} from "./main/note/note.component";
import {CardErrorLogsComponent} from "./main/card/cardErrorLogs.component";
import {CartModule} from "./main/cart/cart.module";
import {CartComponent} from "./main/cart/cart.component";
import {HelpersModule} from "./main/helpers/helpers.module";
import {ActiveVouchersComponent} from "./main/card/activeVouchers.component";
import {MultiSportComponent} from "./main/report/multiSport.component";
import {MedicoverComponent} from "./main/report/medicover.component";
import {ProductSaleReportComponent} from "./main/product/productSaleReport.component";
import {ProductResolve} from "./main/product/providers/product.resolve";
import {ProductTotalSummaryResolve} from "./main/product/providers/productTotalSummary.resolve";
import {ProductTotalSummaryByYearResolve} from "./main/product/providers/productTotalSummaryByYear.resolve";
import {VoucherTemplateSortingComponent} from "./main/voucherTemplate/voucherTemplateSorting.component";
import {DiscountComponent} from "./main/discount/discount.component";
import {DiscountModule} from "./main/discount/discount.module";
import {DiscountFormComponent} from "./main/discount/discountForm.component";
import {DiscountResolve} from "./main/discount/resolvers/discount.resolve";
import {VoucherGroupsResolve} from "./main/voucherTemplateGroup/resolvers/voucherGroups.resolve";
import {WorkTimeModule} from "./main/workTime/workTime.module";
import {WorkTimeReportComponent} from "./main/report/workTime/workTime.component";
import {CustomerResolve} from "./main/customer/resolvers/customer.resolve";
import {VirtualReceiptComponent} from "./main/pos/virtualReceipt.component";
import {HttpCancelService} from "./main/services/httpCancel.service";
import {ManageHttpInterceptor} from "./main/services/manageHttp.interceptor";
import {ServiceResolve} from "./main/service/resolvers/service.resolve";
import {AllResourcesResolve} from "./main/resource/resolvers/resources.resolve";
import {VatRatesResolve} from "./main/vatRate/resolvers/vatRates.resolve";
import {ServiceCategoriesResolve} from "./main/category/resolve/serviceCategories.resolve";
import {CurrentVersionComponent} from "./main/currentVersion/currentVersion.component";
import {CurrentVersionModule} from "./main/currentVersion/currentVersion.module";
import {GroupActivityComponent} from "./main/calendar/groupActivity.component";
import {GroupActivityResolve} from "./main/calendar/resolvers/groupActivity.resolve";
import {ReservationDataResolve} from "./main/calendar/resolvers/reservationData.resolve";
import {TrainerComponent} from "./main/user/trainer.component";
import {TrainerDashboardModule} from "./main/trainerDashboard/trainerDashboard.module";
import {TrainerDashboardComponent} from "./main/trainerDashboard/trainerDashboard.component";
import {CalendarWorkersResolve} from "./main/user/resolvers/calendarWorkers.resolve";
import {CalendarResourceUsersResolve} from "./main/user/resolvers/calendarResourceUsersResolve.resolve";
import {UserIdleComponent} from "./main/user/user-idle/user-idle.component";
import {TabsComponent} from "./main/staticPage/tabs.component";
import {StaticPageModule} from "./main/staticPage/staticPage.module";
import {LogModule} from "./main/log/log.module";
import {LogComponent} from "./main/log/log.component";
import {PzuSportComponent} from "./main/report/pzuSport.component";
import {ProfitComponent} from "./main/report/profit.component";
import {WorkerRechargeCardModule} from "./main/workerRechargeCard/workerRechargeCard.module";
import {SelectedEmpoyeeScheduleComponent} from "./main/employeeSchedule/selectedEmpoyeeSchedule/selectedEmpoyeeSchedule.component";
import {AllResourceTypesResolve} from "./main/resource/resolvers/resourceTypes.resolve";
import {ResourceResolve} from "./main/resource/resolvers/resource.resolve";
import {DashboardComponent} from "./main/dashboard/dashboard.component";
import {TrainingModule} from "./main/training/training.module";
import {TrainingComponent} from "./main/training/training.component";
import {TrainingDetailsComponent} from "./main/training/trainingDetails.component";
import {VouchersReportComponent} from "./main/report/vouchersReport.component";
import {ServicesUseComponent} from "./main/report/servicesUse.component";
import {TrainingCommissionComponent} from "./main/report/trainingCommission.component";

@NgModule({
    declarations: [
        AppComponent,
    ],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        HttpClientModule,
        RouterModule.forRoot(appRoutes),

        TranslateModule.forRoot(),

        // Material moment date module
        MatMomentDateModule,

        // Material
        MatButtonModule,
        MatIconModule,
        MatDialogModule,
        MatTabsModule,

        // Fuse modules
        FuseModule.forRoot(fuseConfig),
        FuseProgressBarModule,
        FuseSharedModule,
        FuseSidebarModule,
        FuseThemeOptionsModule,
        TranslateModule,

        // FullCalendarModule,

        // App modules
        LayoutModule,
        // SampleModule,
        UserModule,
        GroupModule,
        OperatorModule,
        ProfileModule,
        LoginModule,
        DashboardModule,
        CustomerModule,
        SettingsModule,
        DeviceModule,
        DeviceGroupModule,
        CustomerSourceModule,
        ProductModule,
        CategoryModule,
        VatRateModule,
        ManufacturerModule,
        ServiceModule,
        ResourceModule,
        CalendarModule,
        EmployeeScheduleModule,
        OrderModule,
        SupplierModule,
        SupplierOrderModule,
        MatSnackBarModule,
        VoucherTemplateModule,
        ReportModule,
        StorehouseUseModule,
        CountryModule,
        CardModule,
        CardOnlineModule,
        PosModule,
        SignUpActionModule,
        PublicApiAccessModule,
        MessageTemplateModule,
        NotificationAccountModule,
        MarketingActionModule,
        SecurityModule,
        CardStatsModule,
        CmsModule,
        GalleryModule,
        LanguageModule,
        MenuModule,
        PageModule,
        CashReportModule,
        VoucherTemplateGroupModule,
        InternalFileModule,
        ReservationModule,
        NotificationModule,
        NotificationHistoryModule,
        NoteModule,
        CartModule,
        HelpersModule,
        DiscountModule,
        WorkTimeModule,
        CurrentVersionModule,
        TrainerDashboardModule,
        TrainingModule,
        StaticPageModule,
        LogModule,
        WorkerRechargeCardModule,
        AngularCalendarModule.forRoot({provide: DateAdapter, useFactory: adapterFactory}),
    ],
    providers: [
        // HttpCancelService,
        // { provide: HTTP_INTERCEPTORS, useClass: ManageHttpInterceptor, multi: true },
        {provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true},
    ],
    exports: [
        UserIdleComponent,
    ],
    bootstrap: [
        AppComponent,
    ]
})
export class AppModule
{
}
